import { useMutation, useQuery } from '@apollo/client'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { Autocomplete, Button, Stack, TextField } from '@mui/material'
import { gql } from '__generated__'
import { ReferenceEntry, Titles } from '__generated__/graphql'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import { useFormik } from 'formik'
import { useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useParams } from 'react-router-dom'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import DrawerComponent from 'shared/components/DrawerComponent'
import NonFormikPhoneInput from 'shared/components/NonFormikPhoneInput'
import useToast from 'shared/components/Toast/useToast'
import { DropdownOption } from 'shared/models/dropdownOption'
import { formatGraphQLError } from 'shared/utils'
import * as Yup from 'yup'
import { GetCandidateReferences, Reference } from './CandidateReferencesPanel'

const GetCandidateReferenceOptions = gql(/* GraphQL */ `
    query GetCandidateReferenceOptions {
        titles {
            id
            value
        }
    }
`)

export const AddCandidateReferences = gql(/* GraphQL */ `
    mutation AddCandidateReferences($input: AddCandidateReferencesInput!) {
        addCandidateReferences(input: $input) {
            message
            success
        }
    }
`)

export const UpdateCandidateReference = gql(/* GraphQL */ `
    mutation UpdateCandidateReference(
        $candidateReferenceId: ID!
        $reference: ReferenceEntry!
    ) {
        updateCandidateReference(
            candidateReferenceId: $candidateReferenceId
            reference: $reference
        ) {
            message
            success
        }
    }
`)

export const DeleteCandidateReferences = gql(/* GraphQL */ `
    mutation DeleteCandidateReferences($candidateReferenceIds: [ID!]!) {
        deleteCandidateReferences(
            candidateReferenceIds: $candidateReferenceIds
        ) {
            message
            success
        }
    }
`)

/** A type for the reference form that includes a small piece of metadata to determine which response shape to expect. */
interface FormValues extends ReferenceEntry {
    operation: 'add' | 'update'
}

interface CandidateExperienceDrawerProps {
    isOpen: boolean
    onClose?: () => void
    reference: Reference | null
}

export const CandidateReferenceDrawer = ({
    isOpen,
    onClose,
    reference,
}: CandidateExperienceDrawerProps) => {
    const { candidateId } = useParams()
    const toast = useToast()
    const [titles, setTitles] = useState<DropdownOption<Titles>[]>([])

    const validationSchema: Yup.ObjectSchema<ReferenceEntry> = Yup.object({
        companyId: Yup.string().nullable(),
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        firstName: Yup.string().required('First name is required'),
        id: Yup.string().optional(),
        lastName: Yup.string().required('Last name is required'),
        phone: Yup.string().required('Phone number is required'),
        workTitleId: Yup.string().required('Title is required'),
    })

    const initialValues: FormValues = reference
        ? {
              operation: 'update',
              companyId: reference.company?.id ?? null,
              email: reference.email,
              firstName: reference.firstName,
              lastName: reference.lastName,
              phone: reference.phone,
              workTitleId: reference.workTitle?.id ?? '',
          }
        : {
              operation: 'add',
              companyId: null,
              email: '',
              firstName: '',
              lastName: '',
              phone: '',
              workTitleId: '',
          }

    useQuery(GetCandidateReferenceOptions, {
        onCompleted: (data) => {
            setTitles(data.titles)
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to fetch candidate reference options'
                ),
            })
        },
    })

    const handleClose = () => {
        resetForm()
        if (typeof onClose === 'function') onClose()
    }

    const [addReferences] = useMutation(AddCandidateReferences, {
        refetchQueries: [GetCandidateReferences],
        onCompleted: (data) => {
            if (data.addCandidateReferences.success) {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Candidate reference added successfully',
                })

                handleClose()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: `Failed to add candidate reference: ${data.addCandidateReferences.message}`,
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to add candidate reference'
                ),
            })
        },
    })

    const [updateReference] = useMutation(UpdateCandidateReference, {
        refetchQueries: [GetCandidateReferences],
        onCompleted: (data) => {
            if (data.updateCandidateReference.success) {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Candidate reference updated successfully',
                })

                handleClose()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: `Failed to update candidate reference: ${data.updateCandidateReference.message}`,
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to update candidate reference'
                ),
            })
        },
    })

    const [deleteReferences] = useMutation(DeleteCandidateReferences, {
        refetchQueries: [GetCandidateReferences],
        onCompleted: (data) => {
            if (data.deleteCandidateReferences.success) {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Candidate reference deleted successfully',
                })

                handleClose()
            } else {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: `Failed to delete candidate reference: ${data.deleteCandidateReferences.message}`,
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: formatGraphQLError(
                    error,
                    'Failed to delete candidate reference'
                ),
            })
        },
    })

    const handleAddReference = (formValues: ReferenceEntry) => {
        addReferences({
            variables: {
                input: {
                    candidateId: candidateId ?? '',
                    references: [formValues],
                },
            },
        })
    }

    const handleUpdateReference = (formValues: ReferenceEntry) => {
        updateReference({
            variables: {
                candidateReferenceId: reference?.id ?? '',
                reference: formValues,
            },
        })
    }

    const handleDeleteReference = () => {
        deleteReferences({
            variables: {
                candidateReferenceIds: [reference?.id ?? ''],
            },
        })
    }

    const {
        values,
        setFieldValue,
        errors,
        submitForm,
        resetForm,
        handleBlur,
        handleChange,
        touched,
    } = useFormik<FormValues>({
        initialValues: initialValues,
        validationSchema,
        onSubmit: (values) => {
            if (values.operation === 'update') {
                const { operation, ...reference } = values
                handleUpdateReference(reference)
            } else {
                const { operation, ...reference } = values
                handleAddReference(reference)
            }
        },
        enableReinitialize: true,
    })

    const { data } = useQuery(GetCompaniesById, {
        variables: {
            ids: values?.companyId ? [values.companyId] : [],
        },
    })

    return (
        <DrawerComponent
            open={isOpen}
            anchor="right"
            onClose={handleClose}
            hasCancelButton
            onCancel={handleClose}
            primaryActionText={reference ? 'Update' : 'Add'}
            onPrimaryAction={submitForm}
            headerTitle="References"
        >
            <Stack gap={3} alignItems="flex-start">
                {/* First Name */}
                <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    required
                />

                {/* Last Name */}
                <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    required
                />

                {/* Company */}
                <CompaniesAutocomplete
                    fullWidth
                    getOptionLabel={({ value }) => value}
                    value={data?.companiesById[0] ?? null}
                    onChange={(_, newValue) => {
                        setFieldValue('companyId', newValue?.id || null)
                    }}
                    error={touched.companyId && Boolean(errors.companyId)}
                    helperText={
                        touched.companyId ? errors.companyId : undefined
                    }
                />

                {/* Title */}
                <Autocomplete
                    fullWidth
                    options={titles}
                    getOptionLabel={({ value }) => value}
                    value={
                        titles.find(({ id }) => id === values.workTitleId) ||
                        null
                    }
                    onChange={(_, newValue) => {
                        setFieldValue('workTitleId', newValue?.id || '')
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Title"
                            name="workTitleId"
                            error={
                                touched.workTitleId &&
                                Boolean(errors.workTitleId)
                            }
                            helperText={
                                touched.workTitleId && errors.workTitleId
                            }
                            required
                        />
                    )}
                />

                {/* Phone */}
                <NonFormikPhoneInput
                    label="Phone"
                    name="phone"
                    value={values.phone}
                    handleInputChange={(value) =>
                        setFieldValue('phone', value || '')
                    }
                    error={
                        (touched.phone && Boolean(errors.phone)) ||
                        (touched.phone && !isValidPhoneNumber(values.phone))
                    }
                    helperText={
                        (touched.phone && errors.phone) ||
                        (touched.phone &&
                            (!isValidPhoneNumber(values.phone)
                                ? 'Invalid phone number'
                                : ''))
                    }
                    size="small"
                    required
                />

                {/* Email */}
                <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    required
                />

                {/* Delete Button */}
                {reference && (
                    <Button
                        startIcon={<DeleteIcon />}
                        variant="text"
                        color="error"
                        onClick={handleDeleteReference}
                    >
                        Delete Reference
                    </Button>
                )}
            </Stack>
        </DrawerComponent>
    )
}
